import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';

const ARROW_SIZE = 10;
const TOOLTIP_GAP = 16;

function computePosition(rect, placement, tooltipWidth, tooltipHeight) {
  let top = 0;
  let left = 0;
  let arrowTop = '';
  let arrowLeft = '';
  let arrowTransform = '';

  switch (placement) {
    case 'bottom':
      top = rect.bottom + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      arrowTop = `-${ARROW_SIZE}px`;
      arrowLeft = '50%';
      arrowTransform = 'translateX(-50%)';
      break;
    case 'top':
      top = rect.top - tooltipHeight - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      arrowTop = `${tooltipHeight}px`;
      arrowLeft = '50%';
      arrowTransform = 'translateX(-50%) rotate(180deg)';
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - TOOLTIP_GAP;
      arrowTop = '50%';
      arrowLeft = `${tooltipWidth}px`;
      arrowTransform = 'translateY(-50%) rotate(-90deg)';
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + TOOLTIP_GAP;
      arrowTop = '50%';
      arrowLeft = `-${ARROW_SIZE}px`;
      arrowTransform = 'translateY(-50%) rotate(90deg)';
      break;
    default:
      break;
  }

  const pad = 16;
  if (left < pad) left = pad;
  if (left + tooltipWidth > window.innerWidth - pad) left = window.innerWidth - tooltipWidth - pad;
  if (top < pad) top = pad;
  if (top + tooltipHeight > window.innerHeight - pad) top = window.innerHeight - tooltipHeight - pad;

  return {
    top,
    left,
    arrowStyle: { top: arrowTop, left: arrowLeft, transform: arrowTransform },
  };
}

const SpotlightTour = ({
  run,
  onFinish,
  steps = [],
  finishLabel = 'Done',
  onComplete,
  tooltipWidth = 400,
}) => {
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const maskId = useId().replace(/:/g, '');
  const scrolledStepRef = useRef(-1);

  useEffect(() => {
    if (run) {
      setStep(0);
      scrolledStepRef.current = -1;
    }
  }, [run]);

  const finish = useCallback(() => {
    onComplete?.();
    onFinish();
  }, [onComplete, onFinish]);

  const measureTarget = useCallback(() => {
    if (!run || steps.length === 0) {
      setTargetRect(null);
      return undefined;
    }
    const currentStep = steps[step];
    if (!currentStep) {
      setTargetRect(null);
      return undefined;
    }
    const el = document.querySelector(currentStep.target);
    if (el) {
      if (scrolledStepRef.current !== step) {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        scrolledStepRef.current = step;
      }
      setTargetRect(el.getBoundingClientRect());
      return undefined;
    }
    setTargetRect(null);
    return currentStep.target;
  }, [run, step, steps]);

  useEffect(() => {
    let rafId;
    let attempts = 0;
    const missingTarget = measureTarget();

    const retry = () => {
      if (!missingTarget || attempts >= 30) return;
      const el = document.querySelector(missingTarget);
      if (el) {
        if (scrolledStepRef.current !== step) {
          el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
          scrolledStepRef.current = step;
        }
        setTargetRect(el.getBoundingClientRect());
        return;
      }
      attempts += 1;
      rafId = requestAnimationFrame(retry);
    };

    if (missingTarget) {
      rafId = requestAnimationFrame(retry);
    }

    let remeasureId;
    const remeasure = () => {
      const el = document.querySelector(steps[step]?.target);
      if (el) setTargetRect(el.getBoundingClientRect());
    };
    remeasureId = requestAnimationFrame(remeasure);
    const remeasureTimeout = setTimeout(remeasure, 120);

    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (remeasureId) cancelAnimationFrame(remeasureId);
      clearTimeout(remeasureTimeout);
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget, step, steps]);

  useEffect(() => {
    if (tooltipRef.current) {
      const { offsetWidth, offsetHeight } = tooltipRef.current;
      setTooltipSize({ width: offsetWidth, height: offsetHeight });
    }
  }, [step, run, targetRect]);

  useEffect(() => {
    if (!run) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        finish();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (step < steps.length - 1) setStep((s) => s + 1);
        else finish();
      } else if (e.key === 'ArrowLeft') {
        if (step > 0) setStep((s) => s - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [run, step, steps.length, finish]);

  if (!run || steps.length === 0 || !targetRect) return null;

  const currentStep = steps[step];
  const StepIcon = currentStep.icon;
  const isLastStep = step === steps.length - 1;

  const spotPad = 10;
  const spotRect = {
    top: targetRect.top - spotPad,
    left: targetRect.left - spotPad,
    width: targetRect.width + spotPad * 2,
    height: targetRect.height + spotPad * 2,
    borderRadius: 16,
  };

  const pos = computePosition(
    targetRect,
    currentStep.placement,
    tooltipSize.width || tooltipWidth,
    tooltipSize.height || 240
  );

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }}>
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id={maskId}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={spotRect.left}
              y={spotRect.top}
              width={spotRect.width}
              height={spotRect.height}
              rx={spotRect.borderRadius}
              ry={spotRect.borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.5)"
          mask={`url(#${maskId})`}
        />
      </svg>

      <div
        style={{
          position: 'absolute',
          top: spotRect.top - 2,
          left: spotRect.left - 2,
          width: spotRect.width + 4,
          height: spotRect.height + 4,
          borderRadius: spotRect.borderRadius + 2,
          border: '2px solid rgba(16, 185, 129, 0.5)',
          boxShadow: '0 0 20px rgba(16, 185, 129, 0.15), inset 0 0 20px rgba(16, 185, 129, 0.05)',
          pointerEvents: 'none',
        }}
      />

      <div
        ref={tooltipRef}
        className="bg-white dark:bg-gray-900 border border-gray-200/80 dark:border-gray-700/80 rounded-3xl overflow-hidden"
        style={{
          position: 'absolute',
          top: pos.top,
          left: pos.left,
          width: tooltipWidth,
          zIndex: 10001,
          animation: 'tourFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            ...pos.arrowStyle,
            width: 0,
            height: 0,
            borderLeft: `${ARROW_SIZE}px solid transparent`,
            borderRight: `${ARROW_SIZE}px solid transparent`,
            borderBottom: `${ARROW_SIZE}px solid var(--tour-arrow-color, white)`,
            zIndex: 10002,
          }}
          className="[--tour-arrow-color:white] dark:[--tour-arrow-color:#111827]"
        />

        <button
          onClick={finish}
          className="absolute top-4 right-4 p-1.5 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all z-10"
        >
          <X size={16} />
        </button>

        <div className="px-7 pt-7 pb-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800/50 flex items-center justify-center shrink-0">
              <StepIcon size={20} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-base font-black text-gray-900 dark:text-white uppercase tracking-tight leading-none">
                {currentStep.title}
              </h3>
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                {step + 1} of {steps.length}
              </span>
            </div>
          </div>

          <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {currentStep.content}
          </div>
        </div>

        <div className="flex items-center justify-between px-7 py-4 bg-gray-50/80 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step
                    ? 'w-6 bg-emerald-500'
                    : i < step
                    ? 'w-3 bg-emerald-300 dark:bg-emerald-700'
                    : 'w-3 bg-gray-200 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-xl hover:bg-white dark:hover:bg-gray-700 transition-all"
              >
                <ChevronLeft size={14} />
                Back
              </button>
            )}
            <button
              onClick={() => {
                if (isLastStep) finish();
                else setStep((s) => s + 1);
              }}
              className="flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-all shadow-sm shadow-emerald-200 dark:shadow-emerald-900/30"
            >
              {isLastStep ? finishLabel : 'Next'}
              {!isLastStep && <ChevronRight size={14} />}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tourFadeIn {
          from { opacity: 0; transform: scale(0.97) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default SpotlightTour;
